import { useRef, useEffect, useCallback, useState, useMemo } from 'react'
import type { RadarBubble, RadarZones, RadarRay } from '@/services/api/teacher'
import { DEFAULT_TIME_RANGE_DAYS } from '@/utils/radarStorage'
import { useRetinaCanvas } from '../hooks/useRetinaCanvas'
import { useAnimationConfig } from '../hooks/useReducedMotion'
import {
  drawAnimatedBubble,
  computeSplitOffsets,
  computeOverlapOffsets,
  computeAdminFanLayout,
  adminFanActive,
  isAdminCollapsedHidden,
  shouldFrost,
  shouldStartMelt,
  meltAlphaFromElapsed,
  type RenderPosition,
  type RadarSizeConfig,
  clearPositionCache,
} from './BubbleRenderer'

// Error fallback component
function RadarErrorFallback({ onReload }: { onReload?: () => void }) {
  return (
    <div className="flex flex-col items-center justify-center h-full text-center p-4 rounded-xl border border-red-200 bg-red-50">
      <p className="text-red-600 mb-2">Radar konnte nicht geladen werden</p>
      <p className="text-red-500 text-sm mb-4">Bitte laden Sie die Seite neu</p>
      {onReload && (
        <button
          onClick={onReload}
          className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition"
        >
          Neu laden
        </button>
      )}
    </div>
  )
}

interface RadarCanvasProps {
  bubbles: RadarBubble[]
  zones: RadarZones
  rays: RadarRay[]
  config: RadarSizeConfig | null
  timeRange?: { days: number; from: string; to: string }
  width?: number
  height?: number
  onBubbleClick: (bubble: RadarBubble) => void
  onBubbleHover?: (bubble: RadarBubble | null, mousePos?: { x: number; y: number }) => void
  containerRef?: React.RefObject<HTMLDivElement>
  /** Pause animations when off-screen */
  isPaused?: boolean
  /** Currently focused bubble ID for keyboard navigation */
  focusedId?: string | null
  /** bubblenew-v2: Admin-Radar aktiviert Datums-Fächer + Overlap-Deaktivierung */
  radarType?: 'teacher' | 'admin'
  /** bubblenew-v2: kollabierte Lehrer (Badge-Klick) — Panik-Bubbles bleiben sichtbar */
  collapsedTeacherIds?: string[]
}

// zones and rays are used in other components (GlassZones, OrganicRays)
// They are passed here for API consistency but not used directly in canvas

/**
 * Animated Bubble State
 * Extended bubble with animation properties
 */
interface AnimatedBubble extends RadarBubble {
  floatPhase: number
  floatSpeed: number
}

/**
 * Radar Canvas v4 - FIXED VERSION
 *
 * IMPROVEMENTS:
 * - Native event listeners with { passive: false } for touch
 * - Focus indicator for keyboard navigation
 * - Pause animation when isPaused prop is true
 * - Better accessibility support
 * - Fixed prop mutation using WeakMap
 *
 * Phase 4.2: Fixed prop mutation
 */
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
export function RadarCanvas({
  bubbles,
  zones,
  rays: _rays,
  config,
  timeRange,
  width = 600,
  height = 600,
  onBubbleClick,
  onBubbleHover,
  containerRef,
  isPaused = false,
  focusedId,
  radarType = 'teacher',
  collapsedTeacherIds,
}: RadarCanvasProps) {
  // containerRef is accepted for future positioning features but not currently used
  void containerRef

  const [hasError, setHasError] = useState(false)

  const { canvasRef, ctx, logicalWidth, logicalHeight } = useRetinaCanvas(width, height)
  const animConfig = useAnimationConfig()

  // Animation state
  const animationRef = useRef<number>()
  const shouldAnimateRef = useRef(true)
  const animationState = useRef({
    time: 0,
    bubbles: new Map<string, AnimatedBubble>(),
  })

  // Render positions cache for hit testing (Phase 4.2: Non-mutative)
  const renderPositionsRef = useRef<Map<string, RenderPosition>>(new Map())

  // frost-v2 (E14): Frost-Zustands-Map — erkennt gate_pending true→false
  // zwischen zwei Datenständen und klingt ~600ms ab (Schmelz-Animation).
  // Kein Schmelzen bei Erst-Render/Reload (prev === undefined) und bei
  // reduced motion (dann wird kein meltStart gesetzt).
  const frostStateRef = useRef<Map<string, { seen: boolean; meltStart?: number }>>(new Map())

  // Hover state - useRef to avoid animation restarts
  const hoveredBubbleIdRef = useRef<string | null>(null)
  const [hoveredBubbleId, setHoveredBubbleId] = useState<string | null>(null)

  // Get hovered bubble for click handler
  const hoveredBubble = hoveredBubbleId
    ? animationState.current.bubbles.get(hoveredBubbleId) || null
    : null

  // Canvas dimensions
  const centerX = logicalWidth / 2
  const centerY = logicalHeight / 2
  const maxRadius = Math.min(logicalWidth, logicalHeight) / 2 - 60
  const maxHours = (timeRange?.days ?? DEFAULT_TIME_RANGE_DAYS) * 24

  // ==========================================================================
  // bubblenew-v2/v3: Admin-Radar Datums-Fächer
  // - Fan-Layout (Winkelschlitze pro Datum + Radial-Nudge/Cap) ist MEMOISIERT:
  //   es hängt nur von Bubble-Daten + Geometrie ab, nicht vom Animations-Frame.
  // - bubblenew-v3 (B2): Fan setzt Aggregation voraus (adminFanActive) — ohne
  //   Aggregation greift das alte Layout inkl. Overlap-Auflösung.
  // - Overlap-Auflösung (computeOverlapOffsets) ist im Fan-Modus deaktiviert,
  //   solange kein Notfall-Fallback aktiv ist (E5: Quelle des tangentialen
  //   Drifts). Teacher-Radar verhält sich unverändert.
  // ==========================================================================
  const fanEnabled = adminFanActive(radarType, config)
  const fanLayout = useMemo(
    () => (fanEnabled ? computeAdminFanLayout(bubbles, zones, maxHours, maxRadius, config) : null),
    [bubbles, zones, maxHours, maxRadius, config, fanEnabled]
  )
  const fanLayoutRef = useRef(fanLayout)
  useEffect(() => {
    fanLayoutRef.current = fanLayout
    // Fan-Winkel ändern Positionen — Cache invalidieren.
    clearPositionCache()
  }, [fanLayout])

  const collapsedSet = useMemo(
    () => new Set(collapsedTeacherIds ?? []),
    [collapsedTeacherIds]
  )
  const collapsedSetRef = useRef(collapsedSet)
  useEffect(() => {
    collapsedSetRef.current = collapsedSet
  }, [collapsedSet])

  // T6.3: Split/Overlap nicht pro Animations-Frame — nur bei Daten-/Geometriewechsel.
  const splitOffsets = useMemo(
    () => computeSplitOffsets(bubbles, maxRadius, config),
    [bubbles, maxRadius, config]
  )
  const overlapActive = radarType !== 'admin' || !fanEnabled || (config?.admin_fan_overlap_fallback ?? false)
  const overlapOffsets = useMemo(
    () => overlapActive
      ? computeOverlapOffsets(bubbles, zones, maxHours, maxRadius, config, splitOffsets)
      : new Map<string, number>(),
    [overlapActive, bubbles, zones, maxHours, maxRadius, config, splitOffsets]
  )
  const splitOffsetsRef = useRef(splitOffsets)
  const overlapOffsetsRef = useRef(overlapOffsets)
  useEffect(() => {
    splitOffsetsRef.current = splitOffsets
    overlapOffsetsRef.current = overlapOffsets
  }, [splitOffsets, overlapOffsets])

  // Initialize animated bubbles
  useEffect(() => {
    // Clear position cache when bubbles or timeRange changes significantly
    const prevIds = Array.from(animationState.current.bubbles.keys()).sort().join(',')
    const newIds = bubbles.map(b => b.id).sort().join(',')
    if (prevIds !== newIds) {
      clearPositionCache()
    }

    // Add new bubbles
    bubbles.forEach((bubble) => {
      // frost-v2 (E14): Übergang frostig→nicht-frostig startet das Schmelzen
      const frostPrev = frostStateRef.current.get(bubble.id)
      const frostNow = shouldFrost(bubble)
      if (!frostPrev) {
        frostStateRef.current.set(bubble.id, { seen: frostNow })
      } else if (shouldStartMelt(frostPrev.seen, frostNow)) {
        if (animConfig.reducedMotion) {
          frostPrev.seen = false
          frostPrev.meltStart = undefined
        } else {
          frostPrev.seen = false
          frostPrev.meltStart = performance.now()
        }
      } else {
        frostPrev.seen = frostNow
        // Gate wieder aktiv (false→true während des Schmelzens): Melt abbrechen.
        if (frostNow) frostPrev.meltStart = undefined
      }

      if (!animationState.current.bubbles.has(bubble.id)) {
        animationState.current.bubbles.set(bubble.id, {
          ...bubble,
          floatPhase: Math.random() * Math.PI * 2,
          floatSpeed: 0.5 + Math.random() * 0.5,
        })
      } else {
        // Update existing bubble data while preserving animation state
        const existing = animationState.current.bubbles.get(bubble.id)
        if (existing) {
          animationState.current.bubbles.set(bubble.id, {
            ...existing,
            ...bubble,
          })
        }
      }
    })

    // Remove bubbles that no longer exist
    const currentIds = new Set(bubbles.map(b => b.id))
    animationState.current.bubbles.forEach((_, id) => {
      if (!currentIds.has(id)) {
        animationState.current.bubbles.delete(id)
      }
    })

    // frost-v2: Frost-Zustände entfernter Bubbles aufräumen
    frostStateRef.current.forEach((_, id) => {
      if (!currentIds.has(id)) {
        frostStateRef.current.delete(id)
      }
    })

    // Clear render positions for removed bubbles
    renderPositionsRef.current.forEach((_, id) => {
      if (!currentIds.has(id)) {
        renderPositionsRef.current.delete(id)
      }
    })
    // frost-v2: animConfig.reducedMotion steuert, ob ein Melt überhaupt gestartet wird
  }, [bubbles, timeRange, animConfig.reducedMotion])

  // =============================================================================
  // RAF Animation Loop
  // =============================================================================
  useEffect(() => {
    if (!ctx || hasError) return

    const animate = (timestamp: number) => {
      try {
        // Skip rendering if paused (off-screen) or tab hidden
        if (isPaused || !shouldAnimateRef.current) {
          animationRef.current = requestAnimationFrame(animate)
          return
        }

        animationState.current.time = timestamp

        // Clear canvas (transparent - let glass zones show through)
        ctx.clearRect(0, 0, logicalWidth, logicalHeight)

        // Read hover state from ref (doesn't cause re-render)
        const currentHoveredId = hoveredBubbleIdRef.current
        const hasHoveredBubble = currentHoveredId !== null

        // Clear previous render positions
        renderPositionsRef.current.clear()

        // Draw bubbles with floating animation
        // Sort by bubble_radius DESCENDING so smaller bubbles render LAST (on top).
        // Wichtig fuer 3-Wege-Split-Bubbles: die kleine gruen/rote Bubble wird sonst
        // von der grossen orangen (offen) fully ueberdeckt. Klein-oben stellt sicher,
        // dass z.B. die "uebernommen"-Bubble sichtbar und hoverbar bleibt.
        const sortedBubbles = Array.from(animationState.current.bubbles.values())
          .sort((a, b) => b.bubble_radius - a.bubble_radius)

        // Seitliche (tangentiale) Versaetze fuer Split-Bubbles, damit die Rollen
        // einer Angabe nebeneinander statt uebereinander liegen (pixel-basiert).
        const frameSplitOffsets = splitOffsetsRef.current
        const frameOverlapOffsets = overlapOffsetsRef.current

        const panicThreshold = zones.panic_zone.threshold_hours ?? 2

        sortedBubbles.forEach((bubble) => {
          // bubblenew-v2: Kollaps — Bubbles kollabierter Lehrer werden nicht
          // gerendert; Panik-Bubbles (<= panic threshold) bleiben sichtbar und
          // bekommen den Pin-Ring. Da AdminRadar bereits die gefilterten
          // Bubbles durchreicht, gilt das Panik-Keep automatisch nur innerhalb
          // des aktiven Filters (E6).
          if (isAdminCollapsedHidden(bubble, collapsedSetRef.current, panicThreshold)) {
            return
          }
          const isPinned = collapsedSetRef.current.has(bubble.requester_teacher_id)
            && bubble.hours_until_lesson <= panicThreshold

          const isHovered = currentHoveredId === bubble.id
          const isFocused = focusedId === bubble.id
          const isDimmed = hasHoveredBubble && !isHovered

          // frost-v2 (E14): Schmelz-Alpha, solange der Abkling-Läuft; danach
          // Aufräumen, damit die Map nicht wächst.
          const frostState = frostStateRef.current.get(bubble.id)
          let frostAlpha = 1
          if (frostState?.meltStart !== undefined) {
            const elapsed = timestamp - frostState.meltStart
            frostAlpha = meltAlphaFromElapsed(elapsed, animConfig.reducedMotion)
            if (frostAlpha <= 0) {
              frostState.meltStart = undefined
            }
          }

          // Draw bubble and get render position
          const renderPos = drawAnimatedBubble(
            ctx,
            bubble,
            timestamp,
            centerX,
            centerY,
            maxRadius,
            config,
            isHovered,
            isDimmed,
            animConfig.reducedMotion,
            isFocused,
            zones,
            maxHours,
            (frameSplitOffsets.get(bubble.id) ?? 0) + (frameOverlapOffsets.get(bubble.id) ?? 0),
            fanLayoutRef.current?.angles.get(bubble.id),
            fanLayoutRef.current?.radii.get(bubble.id),
            isPinned,
            frostAlpha
          )

          // Store render position for hit testing (non-mutative!)
          renderPositionsRef.current.set(bubble.id, renderPos)
        })

        // Continue animation loop
        animationRef.current = requestAnimationFrame(animate)
      } catch (error) {
        console.error('RadarCanvas: Animation error', error)
        setHasError(true)
      }
    }

    // Start animation
    animationRef.current = requestAnimationFrame(animate)

    // Cleanup
    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current)
      }
    }
  }, [ctx, logicalWidth, logicalHeight, centerX, centerY, maxRadius, config, animConfig.reducedMotion, isPaused, focusedId, hasError, zones, maxHours, radarType])

  // =============================================================================
  // Sync hover ref with state - does NOT restart animation
  // =============================================================================
  useEffect(() => {
    hoveredBubbleIdRef.current = hoveredBubbleId
  }, [hoveredBubbleId])

  // Kollaps: Hover/Klick nicht auf einer jetzt unsichtbaren Bubble belassen.
  useEffect(() => {
    const id = hoveredBubbleId
    if (!id) return
    const bubble = animationState.current.bubbles.get(id)
    if (!bubble) return
    const panicThreshold = zones.panic_zone.threshold_hours ?? 2
    if (isAdminCollapsedHidden(bubble, collapsedSet, panicThreshold)) {
      setHoveredBubbleId(null)
      onBubbleHover?.(null)
    }
  }, [collapsedSet, hoveredBubbleId, zones, onBubbleHover])

  // =============================================================================
  // Event Handlers
  // =============================================================================

  // Handle mouse movement for hit testing
  const handleMouseMove = useCallback((clientX: number, clientY: number) => {
    const canvas = canvasRef.current
    if (!canvas) return

    const rect = canvas.getBoundingClientRect()
    const scaleX = logicalWidth / rect.width
    const scaleY = logicalHeight / rect.height

    const x = (clientX - rect.left) * scaleX
    const y = (clientY - rect.top) * scaleY

    // Hit testing against render positions (from ref, not from bubble object)
    let foundId: string | null = null
    let foundBubble: AnimatedBubble | null = null

    renderPositionsRef.current.forEach((pos, id) => {
      const bubble = animationState.current.bubbles.get(id)
      if (!bubble) return

      const dx = x - pos.x
      const dy = y - pos.y
      const distance = Math.sqrt(dx * dx + dy * dy)

      const hitPadding = Math.max(4, pos.radius * 0.15)
      if (distance <= pos.radius + hitPadding) {
        foundId = id
        foundBubble = bubble
      }
    })

    const newHoveredId = foundId

    // Only update state if changed
    if (newHoveredId !== hoveredBubbleId) {
      setHoveredBubbleId(newHoveredId)
      // Pass screen coordinates for tooltip positioning
      onBubbleHover?.(foundBubble, foundBubble ? { x: clientX, y: clientY } : undefined)
    }
  }, [canvasRef, logicalWidth, logicalHeight, hoveredBubbleId, onBubbleHover])

  const handleMouseLeave = useCallback(() => {
    setHoveredBubbleId(null)
    onBubbleHover?.(null)
  }, [onBubbleHover])

  const handleClick = useCallback(() => {
    if (hoveredBubble) {
      onBubbleClick(hoveredBubble)
    }
  }, [hoveredBubble, onBubbleClick])

  // =============================================================================
  // Visibility Change - Pause when tab hidden
  // =============================================================================
  useEffect(() => {
    const handleVisibilityChange = () => {
      shouldAnimateRef.current = !document.hidden
    }

    document.addEventListener('visibilitychange', handleVisibilityChange)
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange)
  }, [])

  // =============================================================================
  // Context Lost - Handle WebGL context loss
  // =============================================================================
  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return

    const handleContextLost = () => {
      console.warn('RadarCanvas: WebGL context lost')
      setHasError(true)
    }

    // Canvas 2D context doesn't have webglcontextlost, but we can add a general error handler
    const handleError = (e: ErrorEvent) => {
      if (e.message && e.message.includes('context')) {
        console.warn('RadarCanvas: Context error', e.message)
        setHasError(true)
      }
    }

    canvas.addEventListener('webglcontextlost', handleContextLost)
    window.addEventListener('error', handleError)

    return () => {
      canvas.removeEventListener('webglcontextlost', handleContextLost)
      window.removeEventListener('error', handleError)
    }
  }, [canvasRef])

  // =============================================================================
  // NATIVE EVENT LISTENERS - FIXED: Use { passive: false } for touch
  // =============================================================================
  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return

    // Mouse events (React handles these fine)
    const handleMouseMoveEvent = (e: MouseEvent) => {
      handleMouseMove(e.clientX, e.clientY)
    }

    const handleMouseLeaveEvent = () => {
      handleMouseLeave()
    }

    const handleClickEvent = () => {
      handleClick()
    }

    // Touch events - CRITICAL: Must be non-passive to call preventDefault
    const handleTouchStart = (e: TouchEvent) => {
      e.preventDefault() // Prevent scrolling
      const touch = e.touches[0]
      if (touch) {
        handleMouseMove(touch.clientX, touch.clientY)
      }
    }

    const handleTouchMove = (e: TouchEvent) => {
      e.preventDefault() // Prevent scrolling
      const touch = e.touches[0]
      if (touch) {
        handleMouseMove(touch.clientX, touch.clientY)
      }
    }

    const handleTouchEnd = (e: TouchEvent) => {
      e.preventDefault()
      handleClick()
      handleMouseLeave()
    }

    // Add mouse listeners
    canvas.addEventListener('mousemove', handleMouseMoveEvent)
    canvas.addEventListener('mouseleave', handleMouseLeaveEvent)
    canvas.addEventListener('click', handleClickEvent)

    // Add touch listeners with { passive: false }
    canvas.addEventListener('touchstart', handleTouchStart, { passive: false })
    canvas.addEventListener('touchmove', handleTouchMove, { passive: false })
    canvas.addEventListener('touchend', handleTouchEnd, { passive: false })

    // Cleanup
    return () => {
      canvas.removeEventListener('mousemove', handleMouseMoveEvent)
      canvas.removeEventListener('mouseleave', handleMouseLeaveEvent)
      canvas.removeEventListener('click', handleClickEvent)
      canvas.removeEventListener('touchstart', handleTouchStart)
      canvas.removeEventListener('touchmove', handleTouchMove)
      canvas.removeEventListener('touchend', handleTouchEnd)
    }
  }, [canvasRef, handleMouseMove, handleMouseLeave, handleClick])

  // Show error fallback
  if (hasError) {
    return (
      <div
        className="absolute inset-0 flex items-center justify-center"
        style={{ width: `${width}px`, height: `${height}px`, zIndex: 2 }}
      >
        <RadarErrorFallback onReload={() => window.location.reload()} />
      </div>
    )
  }

  return (
    <canvas
      ref={canvasRef}
      className="absolute inset-0 cursor-pointer touch-none"
      style={{
        width: `${width}px`,
        height: `${height}px`,
        zIndex: 2,
        touchAction: 'none',
      }}
      role="application"
      aria-label="Interaktive Visualisierung von Vertretungsanfragen"
      tabIndex={0}
      // Note: Event handlers are attached via useEffect to use native listeners
      // with { passive: false } for touch events
    />
  )
}
